/**
 * The ranger's weapons, one tick: selection, reloading, and which of them the buttons work
 * (02 §7). The knife lives here; `firearms.ts` has the carbine and the suppressed pistol,
 * `heavy.ts` grenades, smoke, the LAW, the time bomb and Cook-off.
 */

import { makeNoise } from '../alert';
import type { Ctx } from '../ctx';
import { damageEnemy } from '../damage';
import { angleDiff, atan2, cos, PI, sin } from '../math';
import { CARBINE, KNIFE, RANGER } from '../tuning';
import type { Ranger, WeaponId } from '../types';
import { fireCarbine, firePistol } from './firearms';
import { isAware, knifeTarget } from './knife';
import { stepCookOff, useHeavy } from './heavy';

const DEG = PI / 180;

function hasAmmoFor(r: Ranger, weapon: WeaponId): boolean {
  switch (weapon) {
    case 'carbine':
    case 'knife':
      return true;
    case 'grenade':
      return r.inventory.grenades > 0;
    case 'law':
      return r.inventory.laws > 0;
    case 'bomb':
      return r.inventory.bombs > 0;
    case 'pistol':
      return r.gear.pistolRounds > 0;
    case 'smoke':
      return r.gear.smoke > 0;
  }
}

function stab(ctx: Ctx): void {
  const w = ctx.world;
  const r = w.ranger;
  r.fireCooldown = KNIFE.cooldown;
  // ROUND7: who the blade finds is `knifeTarget`'s call, shared with the HUD's in-reach cue.
  const e = knifeTarget(w, ctx.statics, r.aim);
  // The blade goes where the man is: at the elbow he can be well off the aim (knife.ts).
  const toward = e ? atan2(e.x - r.x, e.z - r.z) : r.aim;
  if (e) {
    // Silent only if he never knew you were there (02 §6.3).
    const aware = isAware(e);
    if (aware && !w.mods.silentFrontalKnife) makeNoise(ctx, e.x, e.z, KNIFE.loudNoise, 'shout');
    damageEnemy(ctx, e, 99, true);
    r.knifeKills++;
  }
  ctx.events.push({ type: 'knife', x: r.x + sin(toward) * 0.8, z: r.z + cos(toward) * 0.8, hit: e !== null });
}

export function stepWeapons(ctx: Ctx): void {
  const { world: w, cmd } = ctx;
  const r = w.ranger;
  const downed = r.status === 'downed';

  // A burst ends when the trigger is let go (Controlled Pairs counts rounds from there).
  if (!cmd.fire) r.burstRounds = 0;
  // Running, the weapon is down; stopping, it takes a moment to come back up (Snap Shot shortens it).
  if (r.running) r.raiseTicks = Math.round(RANGER.raiseTicksAfterRun * w.mods.weaponRaiseTime);
  const raising = !r.running && r.raiseTicks > 0;
  if (raising) r.raiseTicks--;

  if (stepCookOff(ctx)) return;

  if (cmd.select !== null && !downed && hasAmmoFor(r, cmd.select)) r.weapon = cmd.select;
  // Down, it is the carbine, the knife, or the pistol he already had out (02 §5).
  if (downed && r.weapon !== 'knife' && r.weapon !== 'pistol') r.weapon = 'carbine';

  if (r.reloadTicks > 0) {
    r.reloadTicks--;
    if (r.reloadTicks === 0 && r.inventory.magazines > 0) {
      r.inventory.magazines--;
      r.inventory.roundsInMag = CARBINE.roundsPerMag;
    }
  } else if (cmd.reload && !r.prevReload && r.inventory.magazines > 0 && r.inventory.roundsInMag < CARBINE.roundsPerMag) {
    r.reloadTicks = Math.round(CARBINE.reloadTicks * w.mods.reloadTime);
    ctx.events.push({ type: 'reload' });
  }

  const firePressed = cmd.fire && !r.prevFire;
  const usePressed = cmd.use && !r.prevUse;
  // Hands full: hauling a body, mid-action, or reeling from a hit.
  if (r.action !== 'none' || r.staggerTicks > 0 || r.fireCooldown > 0) return;
  if (r.dragging >= 0) return;

  if (r.weapon === 'knife') {
    if (firePressed) stab(ctx);
    return;
  }
  if (raising) return;
  // The weapon only bears so far from where the body faces; he must come round first.
  const arc = (r.stance === 'crawl' ? RANGER.fireArcProneDeg : RANGER.fireArcStandDeg) * DEG;
  if (Math.abs(angleDiff(r.facing, r.aim)) > arc) return;
  if (r.weapon === 'carbine') {
    if (cmd.fire && r.reloadTicks === 0) fireCarbine(ctx, firePressed);
  } else if (r.weapon === 'pistol') {
    if (firePressed) firePistol(ctx);
  } else if (!downed && (firePressed || usePressed)) {
    useHeavy(ctx, usePressed && !firePressed);
  }
}
